import { clsx } from 'clsx';
import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, TextareaHTMLAttributes, ReactNode } from 'react';

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  error?: string;
  hint?: string;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { label, error, hint, className, id, ...rest },
  ref
) {
  const autoId = useId();
  const inputId = id ?? `f-${autoId}`;
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={inputId} className="field-label">
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={inputId}
        {...rest}
        className={clsx(
          'w-full rounded-md border bg-canvas-white px-3.5 py-3 text-body text-ink placeholder:text-muted-slate',
          'focus:outline-none focus:ring-2 focus:ring-form-focus-violet/40 focus:border-form-focus-violet',
          'transition-colors',
          error ? 'border-error-red' : 'border-hairline hover:border-slate',
          className
        )}
      />
      {hint && !error && <p className="text-micro text-muted-slate">{hint}</p>}
      {error && <p className="text-micro text-error-red">{error}</p>}
    </div>
  );
});

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  error?: string;
  hint?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, error, hint, className, id, ...rest },
  ref
) {
  const autoId = useId();
  const inputId = id ?? `t-${autoId}`;
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={inputId} className="field-label">
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        id={inputId}
        {...rest}
        className={clsx(
          'w-full rounded-md border bg-canvas-white px-3.5 py-3 text-body text-ink placeholder:text-muted-slate resize-y',
          'focus:outline-none focus:ring-2 focus:ring-form-focus-violet/40 focus:border-form-focus-violet',
          'transition-colors min-h-[120px]',
          error ? 'border-error-red' : 'border-hairline hover:border-slate',
          className
        )}
      />
      {hint && !error && <p className="text-micro text-muted-slate">{hint}</p>}
      {error && <p className="text-micro text-error-red">{error}</p>}
    </div>
  );
});
